import {useEffect, useMemo, useState} from "react";
import {Button, Card, Col, Container, Row} from "react-bootstrap";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Stack from "react-bootstrap/Stack";
import {Helmet} from "react-helmet";
import {Link, useLocation} from "react-router-dom";
import API from "../api";
import {useAuth} from "../AuthUser";
import PricingCarousel from "../components/PricingCarousel";
import PricingFaq from "../components/PricingFaq";

// Discount sales page (MGDWD-186): a copy of the regular pricing page
// ("another sales page that would function the same way the current one
// does" — Chris, Slack #engineering Jul 17) with the full price struck
// through, the discounted price next to it, and a countdown to the offer
// deadline. Reached from FSR review comments/emails, or via Chris's manual
// ?code= links generated in the admin panel.

// Stable codes of the two discount plans. Set on the Plan via the admin
// "Code" field (same mechanism WorkWithChris uses) — until the plans exist
// in an environment, the lookups return empty and the cards stay hidden.
const DISCOUNT_PLAN_CODES = {
	pro: "pro-discount",
	elite: "elite-discount",
};

// Stable code of the hidden "Chris Tyler Exclusive" plan (see WorkWithChris.js).
// The "Want to work exclusively with Chris?" button below only shows while
// this plan is active — Chris toggles it off via Admin > Edit Plan when he
// wants to pause the offer without a redeploy (e.g. mid-promo, Slack Jul 24).
const EXCLUSIVE_PLAN_CODE = "chris-tyler-exclusive";

// Same fallback video as the pricing page shows before the instructor loads.
const FALLBACK_VIMEO_ID = 827001005;

function useQuery() {
	const {search} = useLocation();
	return useMemo(() => new URLSearchParams(search), [search]);
}

// Which end state the page is in (MGDWD-224). Exported for tests.
//
// "This link isn't valid" used to be the answer to every dead end, including
// visitors who never used a link: the Robly campaign mails a plain /offer, so
// a logged-out member opening it on their phone was told the link was dead
// and left. The wording accuses the link, so it may only show when a code was
// actually supplied. Everything else gets its own state:
//
//   loading       eligibility not checked yet
//   live          a grant with time remaining — show the plans
//   expired       a real grant existed but its deadline passed
//   invalidCode   a ?code= was supplied and the server knows no live or
//                 expired grant for it (unknown, or over its redemption limit)
//   needsSignIn   no code and nobody logged in — the server was never asked,
//                 so there is nothing to declare dead; invite them in
//   alreadyMember logged in with an active plan and no grant — not the
//                 audience for this page, acknowledge instead of erroring
//   noOffer       logged in, no plan, no grant — plain "nothing right now"
export function resolveOfferState({checked, eligible, deadline, now, code, user}) {
	if (!checked) {
		return "loading";
	}
	const deadlineMs = deadline ? new Date(deadline).getTime() : null;
	if (eligible && deadlineMs !== null && deadlineMs - now > 0) {
		return "live";
	}
	if (deadlineMs !== null) {
		return "expired";
	}
	if (code) {
		return "invalidCode";
	}
	if (!user) {
		return "needsSignIn";
	}
	return user.planType ? "alreadyMember" : "noOffer";
}

function formatCountdown(ms) {
	const totalSeconds = Math.max(Math.floor(ms / 1000), 0);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	return `${hours}h ${minutes}m ${seconds}s`;
}

// Same routing rules as components/PricingButton, but the checkout href
// carries ?code=, so the signup redirect target must be URL-encoded (the
// shared component concatenates it raw and would drop the code).
function OfferButton({sourceId, code}) {
	const auth = useAuth();
	const href = `/plans/${sourceId}${code ? `?code=${encodeURIComponent(code)}` : ""}`;

	if (auth?.user?.planId === sourceId) {
		return <Button variant="light" disabled>Current Plan</Button>;
	}
	if (auth?.user) {
		return <Button href={href} variant="warning">Upgrade</Button>;
	}
	return <Button href={`/signup?r=${encodeURIComponent(href)}`} variant="warning">Get Started Now</Button>;
}

const cadenceMap = {
	MONTHLY: "month",
	ANNUAL: "annual",
};

function insertDecimal(num) {
	return Number((num / 100).toFixed(2));
}

// Same card markup as the pricing page grid. `plan` is the plan being sold;
// `compareAt` is the regular-priced plan shown struck through next to it
// (absent for Premier, which stays at its normal price in this campaign).
function OfferPlanCard({label, plan, compareAt, code}) {
	if (!plan?.sourceId) {
		return null;
	}

	return (
		<Col lg={4} md={12} sm={12} className="mb-lg-0 mb-4">
			<Card className={`${plan.mostPopular ? "border-secondary" : ""}`}>
				<Card.Body>
					<div className="d-grid gap-2">
						<Card.Title className="text-capitalize">
							<Stack direction="horizontal" gap={3}>
								<div className="p-2">{label}</div>
								{plan.mostPopular ?
									<div className="p-2 ms-auto"><Badge bg="success" style={{fontSize: 12}}>Best Value</Badge></div>
									:
									""
								}
							</Stack>
						</Card.Title>
						<div className="d-flex align-items-center gap-2">
							{compareAt && (
								<div className="h2 text-black-50 text-decoration-line-through mb-0">
									${insertDecimal(compareAt.amount)}
								</div>
							)}
							<div className={`fw-bold mb-0${compareAt ? " text-orange" : ""}`} style={{fontSize: "3.2rem", lineHeight: 1.1}}>
								${insertDecimal(plan.amount)}
							</div>
							/<small className="">{cadenceMap[plan.cadence]}</small>
						</div>
						<OfferButton sourceId={plan.sourceId} code={code} />
						<div className="pt-4 mt-4" style={{borderTop: "2px solid #eee"}}>
							<Stack gap={2}>
								<div className="plan-description" dangerouslySetInnerHTML={{__html: plan.description}}>
								</div>
							</Stack>
						</div>
					</div>
				</Card.Body>
			</Card>
		</Col>
	);
}

function Offer() {
	const auth = useAuth();
	const query = useQuery();
	const location = useLocation();
	const code = query.get("code");

	const [eligibility, setEligibility] = useState({checked: false, eligible: false, deadline: null, source: null});
	const [plans, setPlans] = useState({});
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		const tick = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(tick);
	}, []);

	// A member can hold two independent grants: a manual ?code= link and their
	// own personal post-FSR window. A valid code always wins (MGDWD-219): its
	// hour count is a promise ("72 hours" in Chris's comment — the old
	// later-deadline-wins rule counted down the personal window instead, the
	// 92h Devin reported), it is the grant redemptions are recorded against
	// (ceding to the personal window dropped the code at checkout, so campaign
	// sales went unattributed), and it matches the backend's checkDiscountAccess
	// priority. The personal window is the fallback: a member opening an
	// expired or unknown link must still see their own live window, not a dead
	// end.
	useEffect(() => {
		const checks = [];
		if (code) {
			checks.push(
				API.get("plans/discount-eligibility", {params: {code}})
					.then((res) => ({...res.data, source: "code"}))
					.catch(() => null),
			);
		}
		if (auth?.user) {
			checks.push(
				API.get("reviews/discount-eligibility")
					.then((res) => ({...res.data, source: "personal"}))
					.catch(() => null),
			);
		}
		if (!checks.length) {
			setEligibility({checked: true, eligible: false, deadline: null, source: null});
			return;
		}
		Promise.all(checks).then((results) => {
			const found = results.filter(Boolean);
			const valid = found.filter((r) => r.eligible && r.deadline);
			if (valid.length) {
				const pick = valid.find((r) => r.source === "code") || valid[0];
				setEligibility({checked: true, ...pick});
				return;
			}
			// No live grant. Keep the deadline of a real-but-expired grant (the
			// server returns a past deadline for an expired code/window) so the
			// page can say "this offer has ended". A missing deadline means the
			// code is unknown/already used, or there was no grant at all — a
			// different message ("this link isn't valid"), not "ended".
			const past = found.find((r) => r.deadline);
			setEligibility({
				checked: true,
				eligible: false,
				deadline: past ? past.deadline : null,
				source: past ? past.source : null,
			});
		});
	}, [auth?.user, code]);

	useEffect(() => {
		// Same instructor default as InstructorPricing — Chris (15), the only
		// active instructor today. Scoping to his plans matters: several
		// instructors have visible plans with the same names at different
		// prices, so a global lookup can strike through the wrong price.
		API.get("plans/instructor/15").then((res) => {
			const list = res.data?.plans || [];
			// trim(): the live "Pro " plan has a trailing space in its name
			const byName = (name) => list.find((p) => p.planName.trim() === name && p.visible);
			setPlans((prev) => ({
				...prev,
				instructor: res.data?.instructor,
				// Premier stays at its regular price in this campaign but is
				// still shown on the page (Chris: "Premier - Stays $97").
				regularPremier: byName("Premier"),
				regularPro: byName("Pro"),
				regularElite: byName("Elite"),
			}));
		});
		API.get(`plans/code/${DISCOUNT_PLAN_CODES.pro}`)
			.then((res) => setPlans((prev) => ({...prev, discountPro: res.data})));
		API.get(`plans/code/${DISCOUNT_PLAN_CODES.elite}`)
			.then((res) => setPlans((prev) => ({...prev, discountElite: res.data})));
		API.get(`plans/code/${EXCLUSIVE_PLAN_CODE}`)
			.then((res) => setPlans((prev) => ({...prev, chrisExclusive: res.data})))
			.catch(() => setPlans((prev) => ({...prev, chrisExclusive: null})));
	}, []);

	const deadline = eligibility.deadline ? new Date(eligibility.deadline).getTime() : null;
	const remaining = deadline ? deadline - now : null;
	const offerState = resolveOfferState({
		checked: eligibility.checked,
		eligible: eligibility.eligible,
		deadline: eligibility.deadline,
		now,
		code,
		user: auth?.user,
	});
	const isLive = offerState === "live";
	// The sign-in link must bring them back here with ?code= intact —
	// state.from is the Signin page's own return mechanism. The ?r= param is
	// not: Signin gives it FSR semantics and routes to /upload-videos.
	const signinTo = {pathname: "/signin"};
	const vimeoId = plans.instructor?.vimeoId || FALLBACK_VIMEO_ID;
	// Only carry the code into checkout when it is the active grant — the
	// server gives a passed code priority, so sending a dead code alongside a
	// live personal window would wrongly block the purchase.
	const checkoutCode = eligibility.source === "code" ? code : null;

	return (
		<>
			<Helmet>
				<title>Your Exclusive Pricing | MyGolfDNA.com</title>
				<meta name="description" content="Your personal discount on a MyGolfDNA membership."/>
			</Helmet>
			<div id="offer-page">
				<div id="video" style={{backgroundColor: "#fff", paddingTop: 40, paddingBottom: 40}}>
					<Container>
						<h1 className="text-center monument-font-black text-primary text-uppercase">Get full access for less than a single,<br/> in-person lesson</h1>
						<p className="text-black-50 text-center mb-4" style={{fontWeight: 300, fontSize: 24}}>With a Risk-Free 30-Day Money-Back Guarantee</p>
						<Row className="justify-content-center">
							<Col lg={10}>
								<div style={{padding: "56.25% 0 0 0", position: "relative", height: 0, overflow: "hidden", maxWidth: "100%"}}>
									<iframe
										src={`https://player.vimeo.com/video/${vimeoId}?h=521d44fbfb&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
										frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
										style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%"}} title="Why mygolfdna and pocket pro mixed for sales page"/>
								</div>
							</Col>
						</Row>
					</Container>
				</div>
				<div id="plans" style={{backgroundColor: "#fafbfd", paddingTop: 40, paddingBottom: 40}}>
					<Container>
						{offerState === "loading" && (
							<div className="p-4" style={{textAlign: "center"}}><Spinner className="text-orange" animation="border" /></div>
						)}
						{isLive && (
							<p className="text-orange text-center monument-font-black text-uppercase mb-0" style={{fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)", lineHeight: 1.2}}>
								Your exclusive pricing ends in<br/>{formatCountdown(remaining)}
							</p>
						)}
						{offerState === "needsSignIn" && (
							<>
								<p className="text-black-50 text-center mb-4" style={{fontWeight: 300, fontSize: 20}}>
									Sign in to see your personal offer.
								</p>
								<Row className="justify-content-center">
									<Col xs="auto">
										<Button as={Link} to={signinTo} state={{from: location}} variant="warning" size="lg" className="fw-semibold">
											Sign In
										</Button>
									</Col>
								</Row>
							</>
						)}
						{["expired", "invalidCode", "alreadyMember", "noOffer"].includes(offerState) && (
							<>
								<p className="text-black-50 text-center mb-4" style={{fontWeight: 300, fontSize: 20}}>
									{offerState === "expired" && <>This offer has ended. Check out our <Link to="/pricing">regular pricing</Link> instead.</>}
									{offerState === "invalidCode" && <>This link isn’t valid or has already been used. Check out our <Link to="/pricing">regular pricing</Link> instead.</>}
									{offerState === "alreadyMember" && <>You already have an active membership, so this offer doesn’t apply to your account. Looking to change plans? Check out our <Link to="/pricing">regular pricing</Link>.</>}
									{offerState === "noOffer" && <>You don’t have an active offer right now. Check out our <Link to="/pricing">regular pricing</Link> instead.</>}
								</p>
								<Row className="justify-content-center">
									<Col xs="auto">
										<Button as={Link} to="/pricing" variant="warning" size="lg" className="fw-semibold">
											See Current Pricing
										</Button>
									</Col>
								</Row>
								{!auth?.user && (
									<p className="text-black-50 text-center mt-3 mb-0" style={{fontWeight: 300}}>
										Already a member? <Link to={signinTo} state={{from: location}}>Sign in</Link> to see your personal offer.
									</p>
								)}
							</>
						)}
						{isLive && (
							<>
								<Row className="pt-4 justify-content-center">
									<OfferPlanCard
										label="Premier"
										plan={plans.regularPremier}
										code={null}
									/>
									<OfferPlanCard
										label="Pro"
										plan={plans.discountPro}
										compareAt={plans.regularPro}
										code={checkoutCode}
									/>
									<OfferPlanCard
										label="Elite"
										plan={plans.discountElite}
										compareAt={plans.regularElite}
										code={checkoutCode}
									/>
								</Row>
								{plans.chrisExclusive && (
									<Row className="pt-5 justify-content-center">
										<Col xs="auto">
											<Button
												as={Link}
												to="/work-with-chris"
												variant="success"
												size="lg"
												className="fw-semibold"
											>
												Want to work exclusively with Chris?
											</Button>
										</Col>
									</Row>
								)}
							</>
						)}
					</Container>
				</div>
				<div id="testimonials" style={{backgroundColor: "#fafbfd", paddingTop: 40}}>
					<Container>
						<Row>
							<Col>
								<h3 className="text-center monument-font-black text-primary text-uppercase">Testimonials</h3>
								<p className="text-black-50 text-center mb-4" style={{fontWeight: 300, fontSize: 20}}>What Students Are Saying About Our Instruction</p>
								<PricingCarousel />
							</Col>
						</Row>
					</Container>
				</div>
				<div id="faqs" style={{paddingTop: 40, paddingBottom: 40}}>
					<Container>
						<Row className="justify-content-center">
							<Col lg={8}>
								<h3 className="text-center monument-font-black text-primary text-uppercase">Frequently asked Questions</h3>
								<p className="text-black-50 text-center" style={{fontWeight: 300, fontSize: 20}}>Can’t find the answer you’re looking for?
									<br/>Feel free to reach out to us at <b>support@mygolfdna.com</b> – we’re here to help!</p>
								<PricingFaq />
							</Col>
						</Row>
					</Container>
				</div>
			</div>
		</>
	);
}

export default Offer;
